/* Complete the look — quick add
 *
 * Adds via /cart/add.js and re-renders the cart drawer through Shopify's
 * Section Rendering API, so the drawer shows fresh contents even if the theme
 * never refetches it on open.
 *
 * On mobile the picker is moved to <body> while open. iOS Safari attaches a
 * position:fixed element to the compositing layer of any scrolling ancestor,
 * so a panel left inside the horizontally scrolling rail gets repainted with
 * the section during scroll no matter what its z-index says. Relocating it
 * removes the problem at the source rather than fighting the stacking order.
 *
 * If auto-discovery doesn't find your drawer, set CTL_CONFIG.drawerSectionId
 * to the section id (e.g. 'cart-drawer' — the part after 'shopify-section-').
 */
(function () {
  'use strict';

  // The section's <script> tag is emitted once per section instance, and the
  // browser runs a repeated script again. Two copies would mean two click
  // listeners: the second immediately undoes every toggle the first makes and
  // every size tap fires two adds. Only the first copy is allowed to run.
  if (window.__ctlQuickAddInit) return;
  window.__ctlQuickAddInit = true;

  var CTL_CONFIG = {
    // Set to a string to skip auto-discovery, e.g. 'cart-drawer'
    drawerSectionId: null,
    // Elements that ARE the drawer
    drawerSelectors:
      'cart-drawer, #CartDrawer, #cart-drawer, [data-cart-drawer], .cart-drawer, .drawer--cart, #sidebar-cart, .js-cart-drawer',
    // Elements that OPEN the drawer when clicked
    openerSelectors:
      '[data-cart-toggle], [data-drawer-target="cart"], .js-drawer-open-cart, .header__icon--cart, #cart-icon-bubble, .site-header__cart',
    // Extra sections to refresh after an add (cart count bubble, etc.)
    extraSectionSelectors: '#cart-icon-bubble, [data-cart-count], .cart-count-bubble',
    // Below this width the picker becomes a body-level bottom sheet
    mobileQuery: '(max-width: 749px)',
    debug: false
  };

  var mobile = window.matchMedia(CTL_CONFIG.mobileQuery);

  // Tracks the picker currently relocated to <body>, so it can be put back.
  var portal = { picker: null, marker: null, toggle: null, scrollY: 0 };
  var backdrop = null;

  function log() {
    if (CTL_CONFIG.debug) console.log.apply(console, ['[ctl]'].concat([].slice.call(arguments)));
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- Body-level sheet ---------- */

  function getBackdrop() {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.className = 'ctl-backdrop';
    backdrop.hidden = true;
    backdrop.addEventListener('click', function () {
      closeAllPickers(null);
    });
    document.body.appendChild(backdrop);
    return backdrop;
  }

  function lockScroll() {
    portal.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + portal.scrollY + 'px';
    document.body.classList.add('ctl-scroll-locked');
  }

  function unlockScroll() {
    if (!document.body.classList.contains('ctl-scroll-locked')) return;
    document.body.classList.remove('ctl-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, portal.scrollY);
  }

  function openSheet(picker, toggle) {
    if (!mobile.matches || portal.picker === picker) return;

    // A comment node marks the original position so the picker can be
    // returned to exactly where it came from.
    portal.marker = document.createComment('ctl-picker');
    picker.parentNode.insertBefore(portal.marker, picker);
    document.body.appendChild(picker);
    picker.classList.add('ctl-picker--sheet');

    portal.picker = picker;
    portal.toggle = toggle || null;

    getBackdrop().hidden = false;
    lockScroll();
    log('picker moved to body');
  }

  function closeSheet() {
    if (!portal.picker) return;

    if (portal.marker && portal.marker.parentNode) {
      portal.marker.parentNode.insertBefore(portal.picker, portal.marker);
      portal.marker.parentNode.removeChild(portal.marker);
    } else if (portal.picker.parentNode === document.body) {
      // Its original container is gone (a section re-render, say) — drop it
      // rather than leaving an orphan attached to <body>.
      document.body.removeChild(portal.picker);
    }

    portal.picker.classList.remove('ctl-picker--sheet');
    portal.picker = null;
    portal.marker = null;
    portal.toggle = null;

    if (backdrop) backdrop.hidden = true;
    unlockScroll();
    log('picker returned to section');
  }

  /* ---------- Section discovery ---------- */

  function sectionIdOf(element) {
    if (!element) return null;
    var wrapper = element.closest('.shopify-section');
    if (wrapper && wrapper.id.indexOf('shopify-section-') === 0) {
      return wrapper.id.replace('shopify-section-', '');
    }
    return null;
  }

  function sectionsToRefresh() {
    var ids = [];

    if (CTL_CONFIG.drawerSectionId) {
      ids.push(CTL_CONFIG.drawerSectionId);
    } else {
      var drawer = document.querySelector(CTL_CONFIG.drawerSelectors);
      var drawerId = sectionIdOf(drawer);
      if (drawerId) ids.push(drawerId);
    }

    document.querySelectorAll(CTL_CONFIG.extraSectionSelectors).forEach(function (el) {
      var id = sectionIdOf(el);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    log('sections to refresh:', ids);
    return ids;
  }

  function replaceSection(id, html) {
    var target = document.getElementById('shopify-section-' + id);
    if (!target || !html) return false;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.getElementById('shopify-section-' + id);
    target.innerHTML = fresh ? fresh.innerHTML : parsed.body.innerHTML;
    return true;
  }

  /* ---------- Drawer ---------- */

  function openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    var drawer = document.querySelector(CTL_CONFIG.drawerSelectors);
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      log('opened via drawer.open()');
      return true;
    }

    var opener = document.querySelector(CTL_CONFIG.openerSelectors);
    if (opener) {
      opener.click();
      log('opened via opener click', opener);
      return true;
    }

    log('no drawer opener found');
    return false;
  }

  /* ---------- Pickers ---------- */

  function toggleFor(picker) {
    if (portal.picker === picker && portal.toggle) return portal.toggle;
    var item = picker.closest('[data-ctl-item]');
    return item ? item.querySelector('[data-ctl-toggle]') : null;
  }

  function closeAllPickers(except) {
    // Queried from the document, not from inside the section — an open picker
    // may currently be parented to <body>.
    document.querySelectorAll('[data-ctl-picker]').forEach(function (picker) {
      if (picker === except) return;
      var toggle = toggleFor(picker);
      if (picker === portal.picker) closeSheet();
      picker.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      picker.closest('[data-ctl-item]') && picker.closest('[data-ctl-item]').classList.remove('is-picker-open');
    });
  }

  function togglePicker(toggle) {
    var item = toggle.closest('[data-ctl-item]');
    if (!item) return;
    var picker = item.querySelector('[data-ctl-picker]') || (portal.toggle === toggle ? portal.picker : null);
    if (!picker) return;

    var willOpen = picker.hidden;
    closeAllPickers(willOpen ? picker : null);

    picker.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

    if (willOpen) {
      item.classList.add('is-picker-open');
      openSheet(picker, toggle);
      var firstControl = picker.querySelector('button:not([disabled]):not([data-ctl-close])');
      if (firstControl) firstControl.focus();
    } else {
      item.classList.remove('is-picker-open');
      if (portal.picker === picker) closeSheet();
    }
  }

  function showToast(root, message) {
    var toast = (root && root.querySelector('[data-ctl-toast]')) || document.querySelector('[data-ctl-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toast._ctlTimer);
    toast._ctlTimer = setTimeout(function () {
      toast.hidden = true;
    }, 2600);
  }

  /* ---------- Add to cart ---------- */

  function addToCart(button) {
    // A relocated picker sits outside the section, so fall back to the item
    // the open toggle belongs to.
    var root =
      button.closest('[data-ctl-root]') ||
      (portal.toggle && portal.toggle.closest('[data-ctl-root]')) ||
      document.querySelector('[data-ctl-root]');

    var id = button.getAttribute('data-ctl-variant');
    var title = button.getAttribute('data-ctl-product-title') || 'Item';
    if (!id) return;

    var originalText = button.textContent;
    button.classList.add('is-loading');
    button.textContent = '…';

    var sectionIds = sectionsToRefresh();
    var payload = { items: [{ id: Number(id), quantity: 1 }] };

    if (sectionIds.length) {
      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname + window.location.search;
    }

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(data.description || data.message || 'Could not add this item');
          }
          return data;
        });
      })
      .then(function (data) {
        log('added', data);

        button.classList.remove('is-loading');
        button.textContent = originalText;

        // Close (and un-portal) before touching the DOM, so the picker is back
        // in place if its section happens to be one of the re-rendered ones.
        closeAllPickers(null);

        var rendered = false;
        if (data.sections) {
          Object.keys(data.sections).forEach(function (sectionId) {
            if (replaceSection(sectionId, data.sections[sectionId])) rendered = true;
          });
        }
        log('sections re-rendered:', rendered);

        var mode = root ? root.getAttribute('data-after-add') : 'stay';

        if (mode === 'cart') {
          window.location.href = routeRoot() + 'cart';
          return;
        }

        if (mode === 'drawer') {
          var opened = openCartDrawer();
          if (!opened && !rendered) {
            window.location.href = routeRoot() + 'cart';
            return;
          }
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        showToast(root, title + ' added to cart');
      })
      .catch(function (error) {
        log('add failed', error);
        button.classList.remove('is-loading');
        button.textContent = originalText;
        showToast(root, error.message);
      });
  }

  /* ---------- Events ---------- */

  // Selectors are no longer scoped to [data-ctl-root]: an open picker can be
  // parented to <body>, and these data attributes belong to this section only.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-ctl-toggle]');
    if (toggle) {
      event.preventDefault();
      togglePicker(toggle);
      return;
    }

    var variant = event.target.closest('[data-ctl-variant]');
    if (variant) {
      event.preventDefault();
      addToCart(variant);
      return;
    }

    if (event.target.closest('[data-ctl-close]')) {
      event.preventDefault();
      closeAllPickers(null);
      return;
    }

    if (!event.target.closest('[data-ctl-picker]')) {
      closeAllPickers(null);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllPickers(null);
  });

  // Crossing the breakpoint mid-session would leave a sheet stranded at body
  // level with desktop styling.
  var onBreakpointChange = function () {
    closeAllPickers(null);
  };
  if (typeof mobile.addEventListener === 'function') {
    mobile.addEventListener('change', onBreakpointChange);
  } else if (typeof mobile.addListener === 'function') {
    mobile.addListener(onBreakpointChange);
  }
})();